import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCartState,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getQuote, GetQuoteApiParameter } from "@MasterBluePrint/Services/CustomQuoteService";
import { getE4FIP } from "@MasterBluePrint/Services/Eye4FraudService";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import { getQuoteState } from "@MasterBluePrint/Store/Data/Quotes/QuotesSelector";
import checkoutCartTracking from "@MasterBluePrint/Tracking/CheckoutCart/checkoutCartTracking";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface MiniCartStyles {
    wrapper: InjectableCss;
    overlay: InjectableCss;
    closeContainer?: InjectableCss;
    buttonWrapper?: InjectableCss;
    cartButton?: InjectableCss;
    descriptionText?: InjectableCss;
    totalItems?: InjectableCss;
    headingWrapper?: InjectableCss;
    linkWrappingMiniCartImage?: InjectableCss;
    MiniCartImg?: InjectableCss;
    productDetailLink?: InjectableCss;
    priceContainer?: InjectableCss;
    priceVal?: InjectableCss;
    priceUnit?: InjectableCss;
    qtyContainer?: InjectableCss;
    qtyLabel?: InjectableCss;
    qtyVal?: InjectableCss;
    checkoutButton?: ButtonPresentationProps;
    cartButtonPunchOut?: ButtonPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        cart,
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        cartLinesVal: getCurrentCartState(state).value?.cartLines,
        isPunchOutOrder: isPunchOutOrder(cart),
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isCartCheckoutDisabled: isCartLoading || !cart || isCartCheckoutDisabled(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        currentPageUrl: getLocation(state).pathname,
        quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
        isPunchOutUser: isAuthenticated && (session.userRoles || "").indexOf("PunchOut") !== -1,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    doPunchOutCheckout,
    preloadCheckoutShippingData,
};

export interface OwnProps {
    isOpen: boolean;
    closeMiniCart: any;
    updateCartLine?: HandleThunkActionCreator<typeof updateCartLine>;
}

// type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

export const minicartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            padding: 70px 40px 40px 40px;
            position: absolute;
            z-index: 999;
            background: #fff;
            box-shadow: 0 9px 14px 0 rgba(0, 37, 84, 0.24);
            top: 52px;
            width: 502px;
            right: 31px;

            &:before,
            &:after {
                border-bottom-style: solid;
                content: "";
                display: block;
                height: 0;
                position: absolute;
                width: 0;
            }

            &:after {
                right: 11px;
                top: -22px;
            }

            &:after {
                border: 11px solid;
                border-color: transparent transparent #bbb transparent;
                z-index: 98;
            }

            &:before {
                right: 11px;
                top: -20px;
            }

            &:before {
                border: 11px solid;
                border-color: transparent transparent #fff transparent;
                z-index: 99;
            }

            .minicart-name-container {
                width: calc(100% - 128px);
                display: inline-block;
                vertical-align: top;
                margin-left: 20px;
            }

            .minicart-items {
                margin-bottom: 12px;
                padding-bottom: 12px;
                border-bottom: 1px solid #d7d7d7;

                &:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                    border-bottom: none;
                }
            }

            .minicart-item-container {
                max-height: 281px;
                overflow-y: auto;
            }

            @media (max-width: 1024px) {
                padding: 70px 20px 40px 20px;
                width: 502px;
                right: 40px;
            }

            @media (max-width: 767px) {
                padding: 70px 15px 40px 15px;
                width: 340px;
                right: 32px;
            }

            @media (max-width: 360px) {
                padding: 70px 15px 40px 15px;
                width: 320px;
                right: 0;
            }

            &.adjust-top {
                top: 12px;
                right: 85px;
                position: fixed;

                @media (min-width: 1481px) {
                    right: auto;
                    transform: translate(-450px, 0);
                }

                @media (max-width: 1480px) {
                    right: 40px;
                }

                @media (max-width: 767px) {
                    right: 32px;
                }

                @media (max-width: 360px) {
                    right: 0;
                }
            }
        `,
    },
    overlay: {
        css: css`
            display: block;
            position: absolute;
            z-index: 2;
            opacity: 0.32;
            background-color: rgb(0, 37, 84);
            top: 108px;
            left: 0;
            width: 100%;
            height: calc(100% - 108px);

            @media (max-width: 360px) {
                left: 0;
                right: 0;
                width: 380px;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            padding-top: 25px;
            margin-top: 20px;
            border-top: 1px solid #d7d7d7;
        `,
    },
    cartButton: {
        css: css`
            text-transform: capitalize;
            text-decoration: underline;
            width: 49%;
            max-width: 49%;
            display: inline-block;
            cursor: pointer;
            height: 44px;
            line-height: 1;
            transition: all 200ms ease-in-out;
            border-radius: 0.125rem;
            padding: 0 21px;
            border: 2px solid #002554;
            background: transparent;
            color: #002554;
            font-family: Roboto;
            font-size: 0.875rem;
            background: #fff;
            vertical-align: top;
            text-align: center;
            margin-right: 2%;

            span {
                height: 44px;
                line-height: 44px;
                color: #002554 !important;
                vertical-align: baseline;
            }

            &:hover,
            &:active,
            &:focus {
                border-color: #002554;
                border-radius: 0.125rem;
                background-color: #2060ad;
                color: #fff !important;

                span {
                    color: #fff !important;
                }
            }
        `,
    },
    checkoutButton: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: 0.875rem;
            display: inline-block;
            vertical-align: top;
            text-align: center;
            span {
                font-size: 0.875rem;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 0;
                            bottom: 0;
                            width: 49%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                            max-width: 49%;
                        `,
                        css`
                            width: 49%;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                            max-width: 49%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    closeContainer: {
        css: css`
            position: absolute;
            right: 2.25rem;
            top: 24px;
            cursor: pointer;
            .close-label {
                color: #535e64;
                font-family: Roboto;
                font-size: 0.875rem;
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: 0.525rem;
                vertical-align: middle;
            }
            .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1rem;
                height: 1rem;
                svg {
                    height: 1rem;
                    width: 1rem;
                    color: #535e64;
                }
            }
            @media screen and (max-width: 990px) {
                right: 1.25rem;
            }
        `,
    },
    descriptionText: {
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: 22px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 24px;
            margin-bottom: 9px;
        `,
    },
    totalItems: {
        css: css`
            color: #333f48;
            font-family: Lato;
            font-size: 15px;
            font-weight: bold;
            letter-spacing: 0.17px;
            line-height: 18px;
        `,
    },
    headingWrapper: {
        css: css`
            padding-bottom: 10px;
            margin-bottom: 15px;
            border-bottom: 1px solid #d7d7d7;
        `,
    },
    MiniCartImg: {
        css: css`
            height: auto;
            width: 108px;
            border: 1px solid #ececec;
        `,
    },
    linkWrappingMiniCartImage: {
        css: css`
            width: 108px;
            display: inline-block;
            vertical-align: top;
        `,
    },
    productDetailLink: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
            color: #333f48 !important;
            font-family: Lato;
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 21px;

            a,
            span {
                color: #333f48 !important;
            }
        `,
    },
    priceContainer: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    priceVal: {
        css: css`
            opacity: 0.7;
            color: #c63527;
            font-family: Lato;
            font-size: 20px;
            font-weight: bold;
            letter-spacing: -0.38px;
            line-height: 23px;
            margin-right: 5px;
        `,
    },
    priceUnit: {
        css: css`
            color: #333f48;
            font-family: Lato;
            font-size: 14px;
            letter-spacing: 0.04px;
            line-height: 24px;
            text-transform: lowercase;
        `,
    },
    qtyContainer: {
        css: css`
            width: 100%;
        `,
    },
    qtyLabel: {
        css: css`
            color: #333f48;
            font-family: Roboto;
            font-size: 15px;
            font-weight: 600;
            letter-spacing: 0.17px;
            line-height: 23px;
        `,
    },
    qtyVal: {
        css: css`
            color: #7c878e;
            font-family: Roboto;
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0.18px;
            line-height: 23px;
        `,
    },
    cartButtonPunchOut: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: 0.875rem;
            display: inline-block;
            vertical-align: top;
            text-align: center;
            width: 100%;
            max-width: 100%;

            span {
                font-size: 0.875rem;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            max-width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            max-width: 100%;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = minicartStyles;

const MiniCart: FC<Props> = ({
    shouldLoadCart,
    isOpen,
    closeMiniCart,
    cartUrl,
    totalCountDisplay,
    cartLinesVal,
    loadCurrentCart,
    cart,
    history,
    checkoutReviewAndSubmitPageUrl,
    checkoutShippingPageUrl,
    isPunchOutOrder,
    hasQuoteRequiredProducts,
    currentPageUrl,
    quoteState,
    isPunchOutUser,
}) => {
    const [isDisabled, setIsDisabled] = useState(true);
    const [quote, setQuote] = useState<QuoteModel | null>(null);
    const [isQuoteCart, setIsQuoteCart] = useState(false);
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: true, replaceCart: true, getCartLines: true });
        }
    }, [shouldLoadCart]);
    useEffect(() => {
        const quoteValue = quoteState.value;
        if (currentPageUrl.toLowerCase().includes("quote") && quoteValue) {
            setIsQuoteCart(true);
            setQuote(quoteValue);
        }
    }, [quoteState]);

    useEffect(() => {
        const handleExternalCartUpdate = (e: Event) => {
            loadCurrentCart({
                shouldLoadFullCart: true,
                replaceCart: true,
                getCartLines: true,
            });

            const configuratorEvent = e as CustomEvent<any>;
            const detail = configuratorEvent.detail;

            if (detail?.openMiniCart && typeof closeMiniCart === "function") {
                closeMiniCart(false);
            }
        };

        window.addEventListener("externalCartUpdated", handleExternalCartUpdate);

        return () => {
            window.removeEventListener("externalCartUpdated", handleExternalCartUpdate);
        };
    }, []);

    let eleButOneCount;
    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
        eleButOneCount = totalCountDisplay - 1;
    }
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const cartContainsZeroPriceItem = cart?.cartLines?.map(cartLine => cartLine.pricing?.actualPrice).includes(0);
    const cartContainsQuoteRequiredItem = cart?.cartLines?.map(cartLine => cartLine.quoteRequired).includes(true);
    const checkoutHandler = () => {
        closeMiniCart();
        if (cart != null) {
            getE4FIP(cart.orderNumber);
            checkoutCartTracking(cart);
        }
        if (cartContainsZeroPriceItem || cartContainsQuoteRequiredItem) {
            history.push(cartUrl!);
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }
        setQuoteRequiredModalIsOpen(false);
        const { canBypassCheckoutAddress } = cart!;
        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        history.push(checkoutShippingPageUrl!);
    };

    const checkScrollTop = () => {
        const cartElement = document.querySelectorAll('div[data-test-selector="miniCart"]')[0];
        if (cartElement && isOpen && window.pageYOffset > 108) {
            cartElement.classList.add("adjust-top");
        } else if (cartElement && isOpen && window.pageYOffset <= 108) {
            cartElement.classList.remove("adjust-top");
        }
    };

    if (typeof window !== "undefined") {
        window.addEventListener("scroll", checkScrollTop);
    }

    useEffect(() => {
        if (isOpen) {
            checkScrollTop();
            setIsDisabled(true);
        }
    }, [isOpen]);

    const myRef = useRef<HTMLDivElement>(null);
    const handleClickOutside = (e: Event) => {
        if (e.target instanceof HTMLElement && myRef && myRef.current && !myRef.current.contains(e.target)) {
            closeMiniCart();
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    });
    useEffect(() => {
        if (cartLinesVal) {
            setIsDisabled(false);
        }
    }, [cartLinesVal]);

    if (retainedTotalCountToDisplay === 0) {
        return (
            <>
                {isOpen && (
                    <>
                        <Typography as="div" className={`${isOpen ? "active" : ""}`} {...styles.wrapper}>
                            <Typography as="div" onClick={() => closeMiniCart()} {...styles.closeContainer}>
                                <span className="close-label">Close</span>
                                <Icon className="icon-close" src="XCircle" />
                            </Typography>
                            <Typography as="div" {...styles.descriptionText}>
                                {translate(`There are no items in your cart.`)}
                            </Typography>
                        </Typography>
                    </>
                )}
            </>
        );
    }
    function getQuotePrice(parameter: CartLineModel) {
        if (quote && cartLinesVal) {
            return quote?.cartLines?.find(line => line.productId === parameter.productId)?.pricing?.unitNetPriceDisplay;
        }
    }

    return (
        <>
            {isOpen && (
                <div ref={myRef}>
                    <Typography
                        as="div"
                        data-test-selector="miniCart"
                        className={`minicart-container ${isOpen ? "active" : ""}`}
                        {...styles.wrapper}
                    >
                        <Typography as="div" onClick={() => closeMiniCart()} {...styles.closeContainer}>
                            <span className="close-label">Close</span>
                            <Icon className="icon-close" src="XCircle" />
                        </Typography>
                        <Typography as="div" {...styles.headingWrapper}>
                            <Typography {...styles.descriptionText}>
                                {translate("New item added to your Cart")}
                            </Typography>
                            <Typography as="div" {...styles.totalItems}>
                                {retainedTotalCountToDisplay === 1 ? (
                                    <>{translate(`There is ${retainedTotalCountToDisplay} item in your cart`)} </>
                                ) : (
                                    <>{translate(`There are ${retainedTotalCountToDisplay} items in your cart`)} </>
                                )}
                            </Typography>
                        </Typography>
                        <Typography as="div" className="minicart-item-container">
                            {cartLinesVal?.slice(eleButOneCount, totalCountDisplay).map(cartLineVal => (
                                <Typography key={cartLineVal.id} as="div" className="minicart-items">
                                    <Clickable
                                        {...styles.linkWrappingMiniCartImage}
                                        href={cartLineVal?.productUri}
                                        data-test-selector="productImage"
                                    >
                                        <Img
                                            {...styles.MiniCartImg}
                                            width="108"
                                            height="108"
                                            src={cartLineVal?.smallImagePath}
                                            altText={cartLineVal?.altText}
                                        />
                                    </Clickable>
                                    <Typography as="div" className="minicart-name-container">
                                        <Typography as="div" {...styles.priceContainer}>
                                            <Typography {...styles.priceVal}>
                                                {isQuoteCart
                                                    ? getQuotePrice(cartLineVal)
                                                    : cartLineVal?.pricing?.unitNetPriceDisplay}
                                            </Typography>
                                            <Typography {...styles.priceUnit}>
                                                {cartLineVal?.unitOfMeasureDisplay}
                                            </Typography>
                                        </Typography>
                                        <Link
                                            {...styles.productDetailLink}
                                            href={cartLineVal?.productUri}
                                            alt={`See product details for ${cartLineVal?.shortDescription}`}
                                        >
                                            {cartLineVal?.shortDescription}
                                        </Link>
                                        <Typography as="div" {...styles.qtyContainer}>
                                            <Typography {...styles.qtyLabel}>{translate("Qty: ")}</Typography>
                                            <Typography {...styles.qtyVal}>
                                                {FormattedCommaSeparatedValue(cartLineVal?.qtyOrdered)}
                                            </Typography>
                                        </Typography>
                                    </Typography>
                                </Typography>
                            ))}
                        </Typography>
                        <Typography as="div" {...styles.buttonWrapper}>
                            {!isPunchOutUser ? (
                                <Link
                                    href={cartUrl}
                                    {...styles.cartButton}
                                    onClick={() => closeMiniCart()}
                                    disabled={isDisabled}
                                >
                                    {translate("View Cart")}
                                </Link>
                            ) : (
                                <Button
                                    {...styles.cartButtonPunchOut}
                                    onClick={() => {
                                        closeMiniCart();
                                        if (cartUrl) {
                                            window.location.href = cartUrl;
                                        }
                                    }}
                                    disabled={isDisabled}
                                >
                                    {translate("View Cart")}
                                </Button>
                            )}
                            {!isPunchOutUser && (
                                <Button
                                    {...styles.checkoutButton}
                                    data-test-selector="minicartTotal_Checkout"
                                    onClick={checkoutHandler}
                                    disabled={isDisabled}
                                >
                                    {translate("Proceed to Checkout")}
                                </Button>
                            )}
                        </Typography>
                    </Typography>
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
